import { Css, Palette, useTestIds } from "@homebound/beam";
import clamp from "lodash/clamp";
import { RadialBarChart } from "src/components/charts/RadialBarChart";
import { BudgetOverviewTab_ProjectFragment } from "src/routes/projects/budget/components/reports/BudgetOverviewTab.generated";
import { isDefined } from "src/utils";

type AverageDirectMarginWidgetProps = { project: BudgetOverviewTab_ProjectFragment };

export function AverageDirectMarginWidget({ project }: AverageDirectMarginWidgetProps) {
  const tid = useTestIds({});
  const filteredProjects = project.sellableProjects.filter((p) => isDefined(p.directMarginInBasisPoints));
  const averageDirectMargin = filteredProjects.nonEmpty
    ? filteredProjects.sum((p) => p.directMarginInBasisPoints! / 100) / filteredProjects.length
    : 0;
  // Calculate the inclination of the gauge meter based on the average direct margin
  const margin = clamp(averageDirectMargin, 0, 24);
  const gaugeNeedleAngle = -85 + (margin / 24) * 170; // Range from -85 to 85 degrees

  return (
    <div css={Css.w100.mw0.hPx(320).py3.bgWhite.ba.bcGray300.bw2.br8.$}>
      <div {...tid.percentageLabel} css={Css.tal.px3.xl2.pb1.$}>
        {filteredProjects.nonEmpty ? averageDirectMargin.toFixed(2) : "--"}%
      </div>
      <div css={Css.lg.px3.gray700.pb1.df.$}>Average Direct Margin</div>
      <div css={Css.h("60%").w100.$} {...tid.RadialBarChart}>
        <div css={Css.h100.w100.$} {...tid.RadialBarContainer}>
          <RadialBarChart
            dataset={chartData}
            colors={[Palette.Red400, Palette.Orange300, Palette.Yellow200, Palette.Green200, Palette.Green500]}
            loadingColor={[Palette.Gray200, Palette.Gray300, Palette.Gray400, Palette.Gray500, Palette.Gray600]}
            emptyStateTooltipText={
              filteredProjects.isEmpty ? "There are no projects that have started yet." : undefined
            }
            disabled={filteredProjects.isEmpty}
            startAngle={-84}
            endAngle={84}
            margin={{ top: 30, bottom: -140 }}
            innerRadius={0.73}
            enableLabels={true}
            labelsRadiusOffset={1.65}
            label={({ data }) => data.x.split(" ")[0]}
            enableRadialGrid={false}
            enableCircularGrid={false}
            tooltipValueFormat={(data) => data.bar.category}
            radialAxisEnd={{ tickSize: 0, tickPadding: 16, tickRotation: 277 }}
            radialAxisStart={{ tickSize: 0, tickPadding: 0, tickRotation: 180 }}
            circularAxisOuter={{ tickSize: 0, tickPadding: 0, tickComponent: () => null }}
          />
        </div>
        <div css={Css.relative.$}>
          {filteredProjects.nonEmpty && (
            <div
              css={
                Css.absolute
                  .mx("49%")
                  .br8.bgGray700.wPx(10)
                  .hPx(110)
                  .mtPx(-115)
                  .add("transform", `rotate(${gaugeNeedleAngle}deg)`)
                  .add("transformOrigin", "bottom center").$
              }
            />
          )}
        </div>
        <div css={Css.xs2.gray900.ptPx(4).tac.mxa.$}>Direct Margin</div>
      </div>
    </div>
  );
}

const chartData = [
  {
    id: "+24%",
    data: ["Poor (0%)", "Fair (6%)", "Good (12%)", "Great (18%)", "Best"].map((label) => ({ x: label, y: 5 })),
  },
];
